import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import {
  AccountBalanceWalletOutlined,
  ArrowDropDownRounded,
  ArrowDropUpRounded,
  ChevronRightRounded,
  DescriptionOutlined,
  DirectionsBoatOutlined,
  SpeedOutlined,
  TrackChangesOutlined,
  TrendingUpOutlined,
} from '@mui/icons-material';
import { TABLE_FONT_FAMILY } from '../../theme';
import {
  CommercialDashboard,
  DeltaMode,
  KpiKey,
  formatEuro,
  formatRate,
  formatShortDate,
  kpiDelta,
} from '../../utils/commercialDashboard';
import { PanelIcon } from './ConsolePanel';
import { useConsolePalette } from './consolePalette';

type KpiSpec = {
  key: KpiKey;
  label: string;
  icon: React.ReactNode;
  mode: DeltaMode;
  format: (value: number | null) => string;
  to: string;
  hint: string;
};

const formatCount = (value: number | null) => (value == null ? '—' : String(value));

const KPI_SPECS: KpiSpec[] = [
  {
    key: 'projects_in_progress',
    label: 'Projects in progress',
    icon: <DirectionsBoatOutlined />,
    mode: 'count',
    format: formatCount,
    to: '/projects',
    hint: 'Client projects (type 01) On-going and started',
  },
  {
    key: 'open_leads',
    label: 'Open leads',
    icon: <TrackChangesOutlined />,
    mode: 'count',
    format: formatCount,
    to: '/sales/leads',
    hint: 'Leads not won, lost or closed as other',
  },
  {
    key: 'open_proposals',
    label: 'Open proposals',
    icon: <DescriptionOutlined />,
    mode: 'count',
    format: formatCount,
    to: '/sales/proposals',
    hint: 'Proposal projects On-going or on Hold',
  },
  {
    key: 'conversion_rate',
    label: 'Conversion rate',
    icon: <TrendingUpOutlined />,
    mode: 'points',
    format: formatRate,
    to: '/sales/leads',
    hint: 'Won in the period ÷ leads created in the period',
  },
  {
    key: 'pipeline_value',
    label: 'Pipeline value',
    icon: <AccountBalanceWalletOutlined />,
    mode: 'percent',
    format: formatEuro,
    to: '/sales/leads',
    hint: 'Unweighted value of open leads',
  },
  {
    key: 'pipeline_forecast',
    label: 'Pipeline forecast',
    icon: <SpeedOutlined />,
    mode: 'percent',
    format: formatEuro,
    to: '/sales/leads',
    hint: 'Open value × probability to win',
  },
];

type KpiStripProps = {
  dashboard: CommercialDashboard;
  onNavigate: (path: string) => void;
};

const KpiStrip: React.FC<KpiStripProps> = ({ dashboard, onNavigate }) => {
  const palette = useConsolePalette();
  const snapshotLabel = `vs. ${formatShortDate(dashboard.snapshot_date)}`;
  const periodLabel = `vs. ${dashboard.previous_period.label}`;

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(3, minmax(0, 1fr))',
          lg: 'repeat(6, minmax(0, 1fr))',
        },
      }}
    >
      {KPI_SPECS.map((spec) => {
        const kpi = dashboard.kpis[spec.key];
        const delta = kpiDelta(kpi, spec.mode);
        const compareLabel = spec.key === 'conversion_rate' ? periodLabel : snapshotLabel;
        const deltaColor = delta?.direction === 'up'
          ? palette.rise
          : delta?.direction === 'down' ? palette.fall : palette.inkFaint;

        return (
          <ButtonBase
            key={spec.key}
            onClick={() => onNavigate(spec.to)}
            title={spec.hint}
            aria-label={`${spec.label}: ${spec.format(kpi.value)}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              textAlign: 'left',
              gap: 0.75,
              p: 1.75,
              border: `1px solid ${palette.border}`,
              borderRadius: 1,
              bgcolor: palette.surface,
              transition: 'background-color 150ms ease-out, border-color 150ms ease-out',
              '&:hover': { bgcolor: palette.hover },
              '&:hover .kpi-chevron': { color: palette.figure, transform: 'translateX(2px)' },
              '&.Mui-focusVisible': { outline: `2px solid ${palette.figure}`, outlineOffset: 2 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PanelIcon size={28}>{spec.icon}</PanelIcon>
              <Typography
                sx={{ fontSize: '0.78rem', fontWeight: 600, color: palette.inkSoft, flex: 1, lineHeight: 1.25 }}
              >
                {spec.label}
              </Typography>
              <ChevronRightRounded
                className="kpi-chevron"
                fontSize="small"
                sx={{ color: palette.inkFaint, transition: 'transform 150ms ease-out, color 150ms ease-out' }}
              />
            </Box>
            <Typography
              sx={{
                fontFamily: TABLE_FONT_FAMILY,
                fontWeight: 700,
                fontSize: { xs: '1.35rem', lg: '1.5rem' },
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                color: palette.figure,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {spec.format(kpi.value)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 20, color: deltaColor }}>
              {delta?.direction === 'up' && <ArrowDropUpRounded fontSize="small" sx={{ ml: -0.5 }} />}
              {delta?.direction === 'down' && <ArrowDropDownRounded fontSize="small" sx={{ ml: -0.5 }} />}
              <Typography
                variant="caption"
                sx={{ fontFamily: TABLE_FONT_FAMILY, fontWeight: 600, color: 'inherit', mr: 0.75 }}
              >
                {delta ? delta.text : '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: palette.inkFaint }} noWrap>
                {compareLabel}
              </Typography>
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default KpiStrip;
